<template>
  <div class="bg-white mb-[70px] ml-10 rounded-8 border border-gray-50 text-13">
    <div v-if="isLoading" class="flex justify-center items-center py-40">
      <img src="@/assets/img/loading.gif" alt="Loading..." class="h-48" />
      <span class="ml-8 text-gray-600">Loading suppliers...</span>
    </div>
    <table v-else class="w-full">
      <thead>
        <tr class="text-gray-600 text-13 border-b border-gray-50 align-middle h-48 font-normal">
          <td
            v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'supplier')"
            class="max-w-[110px] truncate cursor-pointer"
            @mouseover.stop="hover.supplier = true"
            @mouseleave.stop="hover.supplier = false"
          >
            <div class="flex items-center ml-10">
              <Sorting
                :hide="!hover.supplier"
                :enabled="currentOrder === 'name'"
                :reverse="currentOrderReverse"
                class="pr-4"
                @click.stop="sort('name')"
              />
              Supplier
            </div>
          </td>
          <td
            v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'status')"
            class="max-w-[80px] truncate cursor-pointer"
            @mouseover.stop="hover.status = true"
            @mouseleave.stop="hover.status = false"
          >
            <div class="flex items-center pl-2">
              <Sorting
                :hide="!hover.status"
                :enabled="currentOrder === 'qualified'"
                :reverse="currentOrderReverse"
                class="pr-4"
                @click.stop="sort('qualified')"
              />
              Status
            </div>
          </td>
          <td
            v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'calculation')"
            class="max-w-[110px] truncate cursor-pointer"
            @mouseover.stop="hover.calculation = true"
            @mouseleave.stop="hover.calculation = false"
          >
            <div class="flex items-center">
              <Sorting
                :hide="!hover.calculation"
                :enabled="currentOrder === 'calculation_method'"
                :reverse="currentOrderReverse"
                class="pr-4"
                @click.stop="sort('calculation_method')"
              />
              Calculation
            </div>
          </td>
          <td
            v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'manufacturing')"
            class="max-w-[110px] truncate cursor-pointer"
            @mouseover.stop="hover.process = true"
            @mouseleave.stop="hover.process = false"
          >
            <div class="flex items-center">
              <Sorting
                :hide="!hover.process"
                :enabled="currentOrder === 'technology'"
                :reverse="currentOrderReverse"
                class="pr-4"
                @click.stop="sort('technology')"
              />
              Manufacturing
            </div>
          </td>
          <td
            v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'details')"
            class="max-w-[200px] truncate"
          >
            Details
          </td>
          <td
            v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'lot_size')"
            class="max-w-[110px] truncate"
          >
            Lot Size
          </td>
          <td
            v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_lot')"
            class="text-right pr-32 max-w-[110px] truncate cursor-pointer"
            @mouseover.stop="hover.price = true"
            @mouseleave.stop="hover.price = false"
          >
            <div class="flex items-center justify-end">
              <Sorting
                :hide="!hover.price"
                :enabled="currentOrder === 'target_price_pl_min'"
                :reverse="currentOrderReverse"
                class="pr-4"
                @click.stop="sort('target_price_pl_min')"
              />
              Price per Lot
            </div>
          </td>
          <td
            v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_part')"
            class="text-right pr-32 max-w-[110px] truncate cursor-pointer"
            @mouseover.stop="hover.price = true"
            @mouseleave.stop="hover.price = false"
          >
            <div class="flex items-center justify-end">
              <Sorting
                :hide="!hover.price"
                :enabled="currentOrder === 'target_price_pp_min'"
                :reverse="currentOrderReverse"
                class="pr-4"
                @click.stop="sort('target_price_pp_min')"
              />
              Price per Part
            </div>
          </td>
          <td class="w-40 max-w-[110px]">&nbsp;</td>
          <td class="min-w-64 w-64 max-w-[110px] text-right pr-8">
            <SupplierSelectColumns />
          </td>
        </tr>
      </thead>
      <tbody>
        <template v-for="supplier in selectedSuppliers" :key="supplier">
          <template v-for="price in supplier.priceInfo" :key="price">
            <tr class="align-middle h-56 border-b border-gray-50 hover:bg-gray-50">
              <td class="w-1/8 font-medium">
                <div class="flex items-center gap-8 ml-10">
                  <img class="supplier-logo" :src="supplier.logoUrl" alt="supplier-logo" />
                  <div class="flex flex-col max-w-[120px]">
                    <div class="truncate" :title="supplier.name">{{ supplier.name }}</div>
                    <div class="text-gray-400 text-11 truncate" :title="supplier.contact_email">
                      {{ supplier.contact_email }}
                    </div>
                  </div>
                </div>
              </td>
              <td v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'status')" class="w-1/8">
                <div class="flex justify-start">
                  <SparkBadge v-if="supplier.is_qualified" variant="success" class="text-11 !px-8 !py-2">
                    Qualified
                  </SparkBadge>
                </div>
              </td>
              <td v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'calculation')" class="w-1/8">
                <div
                  v-if="price.calculation_method === 'spark2spark'"
                  class="flex items-center gap-4 truncate"
                  title="LIVE"
                >
                  <img src="@/assets/icons/supplier/line-chart-up-05.svg" /> LIVE
                </div>
                <div
                  v-else-if="price.calculation_method === 'machine_learning' || price.calculation_method === 'ai'"
                  class="flex items-center gap-4 truncate"
                  title="AI Estimated"
                >
                  <img src="@/assets/icons/supplier/stars-02.svg" />AI Estimated
                </div>
                <div
                  v-else-if="price.calculation_method === 'reverse'"
                  class="flex items-center gap-4 truncate"
                  title="Reverse Engineered"
                >
                  <img src="@/assets/icons/supplier/line-chart-up-05.svg" />Reverse Engineered
                </div>
              </td>
              <td v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'manufacturing')" class="w-1/8">
                <div class="flex flex-col">
                  <div class="text-gray-1000 truncate" :title="getProcessDisplayName(price.technology_display)">
                    {{ getProcessDisplayName(price.technology_display) }}
                  </div>
                  <div v-if="price.material" class="flex items-center gap-2 text-gray-400">
                    <span class="truncate" :title="price.material">{{ price.material }}</span>
                    <img
                      v-if="price.is_alternative"
                      src="@/assets/icons/supplier/switch-horizontal-01.svg"
                      class="w-16 h-16"
                      alt="Alternative Material"
                      title="Alternative material option."
                    />
                  </div>
                  <div v-else class="text-gray-400 italic">No material specified</div>
                </div>
              </td>
              <td v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'details')" class="w-1/8">
                <template v-if="price.descriptions && price.descriptions.filter(d => d.value).length">
                  <div
                    v-for="desc in price.descriptions.filter(d => d.value)"
                    :key="desc.label"
                    class="text-gray-1000 text-11 truncate"
                    :title="`${desc.label}: ${desc.value}`"
                  >
                    <span class="font-medium">{{ desc.label }}: </span>
                    <span>{{ desc.value }}</span>
                  </div>
                </template>
                <div v-else class="text-gray-400 italic">No details</div>
              </td>
              <td v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'lot_size')" class="w-1/8">
                <SparkInput
                  v-model.lazy="editableLotSize"
                  name="lot_size"
                  type="number"
                  custom=""
                  class="w-full"
                  :disabled="isAnyPriceLoading"
                  @change="updateLotSize"
                />
              </td>
              <td
                v-if="
                  selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_lot') ||
                  selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_part')
                "
                class="w-1/8 text-left"
              >
                <div class="flex flex-col">
                  <div
                    v-if="
                      isPriceLoading(supplier.uid) &&
                      selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_lot')
                    "
                    class="flex justify-end mr-32"
                  >
                    <SparkSpinner class="h-3 w-3" />
                  </div>
                  <div
                    v-else-if="price.custom_price_text"
                    class="font-normal text-black mr-32 break-words"
                    :title="price.custom_price_text"
                  >
                    {{ price.custom_price_text }}
                  </div>
                  <template
                    v-else-if="
                      price.target_price_pl_min !== null ||
                      selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_lot')
                    "
                  >
                    <div
                      v-if="
                        $formatTwoDecimalPlaces(price.target_price_pl_min) ===
                        $formatTwoDecimalPlaces(price.target_price_pl_max)
                      "
                      class="font-normal text-black mr-32 truncate text-right"
                      :title="
                        price.target_price_pl_min === 0
                          ? '-'
                          : `${$formatTwoDecimalPlaces(price.target_price_pl_min)} €`
                      "
                    >
                      <template v-if="price.target_price_pl_min === 0">-</template>
                      <template v-else
                        >{{ $formatTwoDecimalPlaces(price.target_price_pl_min)
                        }}<span class="text-gray-400"> €</span></template
                      >
                    </div>
                    <div
                      v-else
                      class="font-normal text-black mr-32 truncate pl-10 text-right"
                      :title="
                        price.target_price_pl_min === 0 && price.target_price_pl_max === 0
                          ? '-'
                          : `${
                              price.target_price_pl_min === 0
                                ? '-'
                                : $formatZeroDecimalPlaces(price.target_price_pl_min) + ' €'
                            } - 
                               ${
                                 price.target_price_pl_max === 0
                                   ? '-'
                                   : $formatZeroDecimalPlaces(price.target_price_pl_max) + ' €'
                               }`
                      "
                    >
                      <template v-if="price.target_price_pl_min === 0">-</template>
                      <template v-else
                        >{{ $formatZeroDecimalPlaces(price.target_price_pl_min)
                        }}<span class="text-gray-400"> €</span></template
                      >
                      -
                      <template v-if="price.target_price_pl_max === 0">-</template>
                      <template v-else
                        >{{ $formatZeroDecimalPlaces(price.target_price_pl_max)
                        }}<span class="text-gray-400"> €</span></template
                      >
                    </div>
                  </template>
                  <div
                    class="text-gray-400 text-right mr-32 text-11 truncate"
                    :title="$dayjs(price.updated).locale('de').format('DD.MM.YYYY')"
                  >
                    {{ $dayjs(price.updated).locale('de').format('DD.MM.YYYY') }}
                  </div>
                </div>
              </td>
              <td v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_part')" class="w-1/8">
                <div class="flex flex-col">
                  <div
                    v-if="
                      isPriceLoading(supplier.uid) &&
                      selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_pert')
                    "
                    class="flex justify-end mr-32"
                  >
                    <SparkSpinner class="h-3 w-3" />
                  </div>
                  <div
                    v-else-if="price.custom_price_text"
                    class="font-normal text-right text-black mr-32 truncate"
                    :title="price.custom_price_text"
                  >
                    {{ price.custom_price_text }}
                  </div>
                  <template
                    v-else-if="
                      price.target_price_pp_min !== null &&
                      selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_part')
                    "
                  >
                    <div
                      v-if="
                        $formatTwoDecimalPlaces(price.target_price_pp_min) ===
                        $formatTwoDecimalPlaces(price.target_price_pp_max)
                      "
                      class="font-normal text-right text-black mr-32 truncate"
                      :title="
                        price.target_price_pp_min === 0
                          ? '-'
                          : `${$formatTwoDecimalPlaces(price.target_price_pp_min)} €`
                      "
                    >
                      <template v-if="price.target_price_pp_min === 0">-</template>
                      <template v-else
                        >{{ $formatTwoDecimalPlaces(price.target_price_pp_min)
                        }}<span class="text-gray-400"> €</span></template
                      >
                    </div>
                    <div
                      v-else
                      class="font-normal text-right text-black mr-32 truncate"
                      :title="
                        price.target_price_pp_min === 0 && price.target_price_pp_max === 0
                          ? '-'
                          : `${
                              price.target_price_pp_min === 0
                                ? '-'
                                : $formatZeroDecimalPlaces(price.target_price_pp_min) + ' €'
                            } - 
                               ${
                                 price.target_price_pp_max === 0
                                   ? '-'
                                   : $formatZeroDecimalPlaces(price.target_price_pp_max) + ' €'
                               }`
                      "
                    >
                      <template v-if="price.target_price_pp_min === 0">-</template>
                      <template v-else
                        >{{ $formatZeroDecimalPlaces(price.target_price_pp_min)
                        }}<span class="text-gray-400"> €</span></template
                      >
                      -
                      <template v-if="price.target_price_pp_max === 0">-</template>
                      <template v-else
                        >{{ $formatZeroDecimalPlaces(price.target_price_pp_max)
                        }}<span class="text-gray-400"> €</span></template
                      >
                    </div>
                  </template>
                  <div
                    v-if="selectedSupplierPanelTableColumns.some(col => col.sorting === 'price_per_part')"
                    class="text-gray-400 mr-32 text-11 truncate text-right"
                    :title="$dayjs(price.updated).locale('de').format('DD.MM.YYYY')"
                  >
                    {{ $dayjs(price.updated).locale('de').format('DD.MM.YYYY') }}
                  </div>
                </div>
              </td>
              <td class="w-1/8">
                <SparkButtonIcon icon="fa-regular fa-paper-plane" @click.stop="shareSingleRFQViaMail(supplier.uid)" />
              </td>
              <!-- Extra column for SupplierSelectColumns, empty in body -->
              <td class="w-40 max-w-[110px] text-right pr-8"></td>
            </tr>
          </template>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import { saveAs } from 'file-saver';
import { mapState, mapActions, mapMutations } from 'vuex';
import 'dayjs/locale/de';
import alternativeIcon from '@/assets/icons/supplier/switch-horizontal-01.svg';

import SupplierSelectColumns from './components/SupplierSelectColumns.vue';
import SupplierTitle from './components/SupplierTitle.vue';

import SparkButtonIcon from '@/components/SparkComponents/SparkButtonIcon.vue';
import SparkInput from '@/components/SparkComponents/SparkInput.vue';
import Sorting from '@/components/Reusable/Sorting.vue';
import SparkBadge from '@/components/SparkComponents/SparkBadge.vue';
import SparkSpinner from '@/components/SparkComponents/SparkSpinner.vue';

export default {
  name: 'SupplierPanelFrame',

  components: {
    SparkButtonIcon,
    SparkInput,
    Sorting,
    SparkBadge,
    SparkSpinner,
    SupplierSelectColumns,
    SupplierTitle,
  },

  data() {
    return {
      isLoading: true,
      editableLotSize: this.part?.lot_size || 1,
      alternativeIcon,
      currentOrder: 'name',
      currentOrderReverse: false,
      hover: {
        supplier: false,
        calculation: false,
        process: false,
        price: false,
        status: false,
      },
      loadingPrices: {},
    };
  },

  computed: {
    ...mapState('application', ['axiosInstance', 'axiosInstanceFileUpload']),
    ...mapState(['part', 'triggerRFQ', 'user']),
    ...mapState('supplier', ['selectedSuppliers', 'selectedFilters']),

    partId() {
      return this.part.part_id;
    },

    supplierPanelTableColumns() {
      return this.user?.user_settings?.supplier_panel_table_columns;
    },

    selectedSupplierPanelTableColumns() {
      let shown = [];
      for (let col in this.supplierPanelTableColumns) {
        if (this.supplierPanelTableColumns[col].selected === true) {
          shown.push(this.supplierPanelTableColumns[col]);
        }
      }

      shown.sort((a, b) => {
        return a.index - b.index;
      });
      return shown;
    },

    isAnyPriceLoading() {
      return Object.values(this.loadingPrices).some(isLoading => isLoading === true);
    },

    processSynonyms() {
      return this.user?.organization?.process_synonyms || {};
    },
  },

  watch: {
    selectedSuppliers: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.isLoading = false;
        }
      },

      immediate: true,
    },

    partId: {
      handler(newPartId) {
        if (newPartId) {
          this.isLoading = true;
          this.getInstantPriceData({ partId: newPartId }).finally(() => {
            setTimeout(() => {
              this.isLoading = false;
            }, 500);
          });
        }
      },

      immediate: true,
    },

    triggerRFQ(newVal) {
      if (newVal != 0) {
        this.shareRFQViaMail();
      }
    },

    'part.lot_size': {
      handler(newVal, oldVal) {
        if (newVal) {
          this.editableLotSize = newVal;
        }

        if (newVal != oldVal) {
        }
      },

      immediate: true,
    },
  },

  methods: {
    ...mapMutations(['updatePart', 'updateUserSettings']),
    ...mapActions('supplier', ['getInstantPriceData', 'requestPriceData']),
    ...mapMutations('supplier', ['setSelectedFilters']),

    getProcessDisplayName(technologyDisplay) {
      const synonym = Object.values(this.processSynonyms).find(
        syn => syn.original_prc_name === technologyDisplay
      );
      
      return synonym ? synonym.prc_name : technologyDisplay;
    },

    shareSingleRFQViaMail(uid) {
      // Create a temporary checked suppliers object with just this supplier
      const checkedSuppliers = { [uid]: true };
      this.shareRFQViaMail(checkedSuppliers);
    },

    async shareRFQViaMail(checkedSuppliers = {}) {
      this.$root.notify('success', 'RFQ Mail', 'Generating content', 3000);

      let formData = { uids: Object.keys(checkedSuppliers) };
      this.axiosInstance
        .post('/api/v1/part/' + this.part.part_id + '/mail-request-for-quotation/', formData)
        .then(response => {
          // take MailTo content from response data and put it in a fresh mail
          window.open(response.data.mailto);

          if (response.data.supplier_type == 'email') {
            this.axiosInstance
              .get('/api/v1/part/' + this.part.part_id + '/zip-request-for-quotation/', {
                responseType: 'blob',
              })
              .then(response => {
                const fileName = this.part.name.replace(/\s/g, '_') + '_data_for_RFQ.zip';
                saveAs(response.data, fileName);
              })
              .catch(error => {
                this.$root.notifyError('CAD Download', error);
              });
          }
        })
        .catch(error => {
          this.$root.notifyError('RFQ Mail', error);
        });
    },

    updateLotSize() {
      // Ensure value is a positive integer
      let lotSize = Math.max(1, Math.round(Number(this.editableLotSize)));

      // Limit to reasonable maximum
      if (lotSize > 1000000) {
        lotSize = 1000000;
        this.$root.notify(
          'warning',
          'Quantity limit reached',
          'You have reached the quantity limit of 1.000.000.',
          3000
        );
      }

      this.editableLotSize = lotSize;

      this.selectedSuppliers.forEach(supplier => {
        this.loadingPrices[supplier.uid] = true;
      });

      this.axiosInstance
        .put(`/api/v1/part/${this.partId}/`, { lot_size: lotSize })
        .then(response => {
          this.$root.notify('success', 'Lot Size', 'Lot size updated successfully', 3000);
          this.updatePart(response.data);

          Promise.all([
            this.getInstantPriceData({ partId: this.partId }),
            this.requestPriceData({
              partId: this.part.part_id,
              data: { mat_id: this.selectedFilters.mat_id, prc_acronym: this.selectedFilters.prc_acronym },
            }),
          ]).finally(() => {
            this.selectedSuppliers.forEach(supplier => {
              this.loadingPrices[supplier.uid] = false;
            });
          });
        })
        .catch(error => {
          this.$root.notifyError('Lot Size Update', error);
          this.editableLotSize = this.part.lot_size;

          this.selectedSuppliers.forEach(supplier => {
            this.loadingPrices[supplier.uid] = false;
          });
        });
    },

    isPriceLoading(supplierUid) {
      return this.loadingPrices[supplierUid] === true;
    },

    handleImageError(e) {
      console.error('Failed to load icon:', e);
    },

    sort(field) {
      if (this.currentOrder === field) {
        this.currentOrderReverse = !this.currentOrderReverse;
      } else {
        this.currentOrder = field;
        this.currentOrderReverse = false;
      }

      if (field === 'name' || field === 'status') {
        const sortedSuppliers = [...this.selectedSuppliers].sort((a, b) => {
          if (field === 'name') {
            return a.name.localeCompare(b.name);
          } else if (field === 'status') {
            return (b.is_qualified ? 1 : 0) - (a.is_qualified ? 1 : 0);
          }
          return 0;
        });

        if (this.currentOrderReverse) {
          sortedSuppliers.reverse();
        }

        this.$store.commit('supplier/setSelectedSuppliers', sortedSuppliers);
        return;
      }

      this.requestPriceData({
        partId: this.part.part_id,
        data: {
          mat_id: this.selectedFilters.mat_id,
          prc_acronym: this.selectedFilters.prc_acronym,
          order_by: this.currentOrder,
          order_reverse: this.currentOrderReverse,
        },
      });
    },
  },
};
</script>

<style>
.supplier-logo {
  height: 30px;
  width: auto;
  max-width: 80px;
  object-fit: contain;
}
</style>
